import React, { useEffect, useState, useRef, useImperativeHandle, useMemo } from 'react';
import { Tooltip, Badge, Button, Tour } from 'antd';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import NP from 'number-precision';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@/components';
import PageTabs from '../PageTabs';
import { useTabNavigate } from '@/hooks/useTabNavigate';
import NoticeDrawer from '@/layout/NoticeDrawer';
import { CommonStore } from '@/stores';
import { getApplicationUrl } from '@/const/serviceEnv';
import { PLATFORM_TYPES } from '@/const/csc';
import { PlusCircleOutlined } from '@ant-design/icons';

import type { MenuDataItem } from '@/const/route.config';

import './index.less';

interface SubMenuProps {
  selectionRouter?: string;
  currentMenu?: MenuDataItem;
  matchMenu?: MenuDataItem[];
  matchMenuKeys?: string[];
  tourFinish?: () => void;
}

export interface SiderMenuRef {
  setIsTourVisible: (visible: boolean) => void;
}

const SiderMenu = React.forwardRef<SiderMenuRef, SubMenuProps>(
  ({ selectionRouter, currentMenu, matchMenu, matchMenuKeys, tourFinish }, ref) => {
    const navigator = useTabNavigate();
    const navigate = useNavigate();

    const { csInfo } = CommonStore;
    const hasCscRights =
      csInfo.csRight && ((csInfo.hasSeat && csInfo.seatStatus === 1) || csInfo.isAdmin);
    const [messageOpen, setMessageOpen] = useState<boolean>(false);
    const [messageNum, setMessageNum] = useState<number | null>(null);
    const [isTourVisible, setIsTourVisible] = useState(false);

    // Tour refs
    const firstMenuRef = useRef<HTMLDivElement>(null);
    const secondMenuRef = useRef<HTMLDivElement>(null);
    const thirdMenuRef = useRef<HTMLDivElement>(null);
    const fourthMenuRef = useRef<HTMLDivElement>(null);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      setIsTourVisible,
    }));

    // Tour steps
    const steps = useMemo(() => {
      return [
        {
          title: '商城管理（1/4）',
          description: <div style={{ height: 50 }}>在这里您可以创建和管理您的商城，快速搭建</div>,
          style: { width: 360 },
          target: () => firstMenuRef.current as HTMLElement,
          prevButtonProps: {
            type: 'text',
          },
          nextButtonProps: {
            onClick: () => {
              selectionRouter && navigate(selectionRouter);
            },
          },
        },
        {
          title: '选品中心（2/4）',
          description: <div style={{ height: 50 }}>在这里，有海量商品、优质专题供您挑选，快捷实现商品一键上架至商城</div>,
          style: { width: 360 },
          target: () => secondMenuRef.current as HTMLElement,
          prevButtonProps: {
            type: 'text',
            onClick: () => {
              navigate('/module/lgjMall/createShop');
            },
          },
          nextButtonProps: {
            onClick: () => {
              navigate('/module/lgjMall/fitmentMall');
            },
          },
        },
        {
          title: '模版中心（3/4）',
          description: <div style={{ height: 70 }}>在这里，有各种不同风格的商城模版，满足您不同节假日主题运营装修需求，并提供高利润率和转换率的商品帮助您快速搭建商城</div>,
          style: { width: 360 },
          target: () => thirdMenuRef.current as HTMLElement,
          prevButtonProps: {
            type: 'text',
            onClick: () => {
              selectionRouter && navigate(selectionRouter);
            },
          },
          nextButtonProps: {
            onClick: () => {
              navigate('/module/lgjMall/market/1');
            },
          },
        },
        {
          title: '商城扩展应用（4/4））',
          description: <div style={{ height: 50 }}>在这里，有红包雨、大转盘等丰富的互动营销玩法满足您的各类活动需求，助您提升商城的运营数据</div>,
          style: { width: 360 },
          target: () => fourthMenuRef.current as HTMLElement,
          prevButtonProps: {
            type: 'text',
            onClick: () => {
              navigate('/module/lgjMall/fitmentMall');
            },
          },
          nextButtonProps: {
            children: '我知道了',
          },
        },
      ];
    }, [selectionRouter]);

    // 获取消息数量
    const getMessageCountNum = async () => {
      const param = {
        channelType: 10,
        isRead: -10, // 未读数量
      };
      const data: any = await CommonStore.remindCountNum(param);

      setMessageNum(NP.plus(data?.systemNum, data?.businessNum));
    };

    useEffect(() => {
      getMessageCountNum();
    }, []);

    const firstMenu = matchMenu?.[0];
    const subMenus = firstMenu?.children || [];

    if (firstMenu?.hideChildrenInMenu || subMenus.length === 0 || currentMenu?.pureMenu)
      return null;

    if (subMenus?.length === 0) return null;

    const { showPageTab, menuType } = JSON.parse(decodeURIComponent(firstMenu?.props || '{}'));

    let activeMenu: MenuDataItem | undefined;
    let activeMenuChild: MenuDataItem[] = [];

    if (menuType === 'twoRowMenu') {
      activeMenu = subMenus?.find((item) => matchMenuKeys?.includes(item.path));
      activeMenuChild = activeMenu?.children || [];
    }

    const collapsed =
      (!activeMenu?.children?.length || activeMenu?.hideChildrenInMenu === true) &&
      menuType !== 'oneRowMenu';

    const style = collapsed ? { width: 64 } : { width: 180 };

    const getTourRef = (path: string) => {
      if (path === '/module/lgjMall/createShop') {
        return firstMenuRef;
      }
      if (path === selectionRouter) {
        return secondMenuRef;
      }
      if (path === '/group/shop/template') {
        return thirdMenuRef;
      }
      if (path === '/group/market') {
        return fourthMenuRef;
      }
      return null;
    };

    const getTourBox = (path: string): React.ReactNode => {
      if (path === '/module/lgjMall/createShop') {
        // return <div ref={firstMenuRef} style={{ height: 88 }} className="fle-tour-box" />;
        return null;
      }
      if (path === selectionRouter) {
        return <div ref={secondMenuRef} style={{ height: 160 }} className="fle-tour-box" />;
      }
      return <React.Fragment />;
    };

    const oneRowMenuCellCom = (item: MenuDataItem) => (
      <div
        key={item.path}
        onClick={() => navigator(item.redirect || item.path)}
        className={classNames('fle-layout-sider-one-menu-cell', {
          active: matchMenuKeys?.includes(item.path),
        })}
      >
        {getTourBox(item.path)}
        {item.icon && <Icon type={item.icon as string} className="menu-icon-default" />}
        {item.name}
      </div>
    );

    const oncRowMenuGroupDom = (item: MenuDataItem) => (
      <div key={item.path} ref={getTourRef(item.path)} className="fle-layout-sider-one-menu-group">
        <div className="fle-layout-sider-one-menu-group-name">{item.name}</div>
        {item.children?.map((route) => oneRowMenuCellCom(route))}
      </div>
    );

    const oncRowMenuBtnDom = (item: MenuDataItem) => {
      let dom: React.ReactNode = <React.Fragment />;

      switch (item.path) {
        case '/module/lgjMall/createShop':
          dom = (
            <div className='btn-sider_item btn-inlined' onClick={() => navigator(item.redirect || item.path)}>
              <PlusCircleOutlined />
              <span>创建商城</span>
            </div>
          );
          break;
        case '/module/lgjMall/application':
          dom = (
            <div className='btn-sider_item btn-outlined' onClick={() => navigator(item.redirect || item.path)}>
              <Icon type="icon-13-shangchengsaas" className="fle-icon-shop" />
              <span>管理商城</span>
            </div>
          );
          break;
        case '/group/product':
          dom = (
            <img
              onClick={() => navigator('/module/lgjProduct/index')}
              src="https://oss.elebuys.com/wowcarp_market.png"
              alt="妙鲤集市"
            />
          );
          break;
        case '/module/program/chat':
          dom = (
            <img
              onClick={() => navigator('/module/program/chat')}
              style={{ borderRadius: 8 }}
              src="https://oss.elebuys.com/resource/goods/materials/202406051526130005155496.png"
              alt="AI制作方案"
            />
          );
          break;
        default:
          dom = (
            <Button block type="default" onClick={() => navigator(item.redirect || item.path)}>
              {item.name}
            </Button>
          );
          break;
      }

      return (
        <div key={item.path} className="fle-layout-sider-one-menu-btn">
          {getTourBox(item.path)}
          {dom}
        </div>
      );
    };

    return (
      <>
        {menuType && (
          <div className="fle-layout-sider">
            <div className="fle-layout-sider-space" style={style} />
            {menuType === 'oneRowMenu' && (
              <div className="fle-layout-sider-one-menu fle-scroll">
                <div className="fle-layout-sider-one-menu-list">
                  {subMenus?.map((item) => {
                    if (item.hideInMenu) {
                      return null;
                    }

                    const { menuType } = JSON.parse(decodeURIComponent(item?.props || '{}'));

                    let dom: React.ReactNode = <React.Fragment />;
                    switch (menuType) {
                      case 'btn':
                        dom = oncRowMenuBtnDom(item);
                        break;
                      case 'group':
                        dom = oncRowMenuGroupDom(item);
                        break;
                      default:
                        dom = oneRowMenuCellCom(item);
                        break;
                    }
                    return dom;
                  })}
                </div>
              </div>
            )}

            {menuType === 'twoRowMenu' && (
              <>
                <div className="fle-layout-sider-wrap">
                  <div className="fle-layout-sider-first-menu fle-scroll">
                    {subMenus
                      ?.filter((item) => !item.hideInMenu)
                      .map((item) => {
                        console.log(item)
                        const active = matchMenuKeys?.includes(item.path);
                        const selectedIcon = JSON.parse(
                          decodeURIComponent(item.props || '{}'),
                        )?.selectedIcon;

                        return (
                          <span
                            key={item.path}
                            onClick={() => navigator(item.redirect || item.path)}
                            className={classNames('fle-layout-sider-first-menu-item', {
                              active,
                            })}
                          >
                            {item.icon && <Icon type={item.icon as string} className="menu-icon" />}
                            {/* {selectedIcon && (
                              <Icon type={selectedIcon} className="selected-menu-icon" />
                            )} */}
                            <span>{item.name}</span>
                          </span>
                        );
                      })}
                  </div>
                  <div className="fle-layout-sider-actions">
                    {/* 此处客服暂时隐藏 */}
                    {hasCscRights && (
                      <Tooltip title="商城客服" placement="right">
                        <Icon
                          type="icon-kefu-moren"
                          className="fle-layout-sider-action"
                          onClick={() => {
                            const cscUrl = getApplicationUrl('csc');
                            const params: any = {
                              platform_type: PLATFORM_TYPES.carpunion_cs,
                              token: Cookies.get('token') || '',
                              redirectUrl: encodeURIComponent(window.location.origin),
                            };
                            const paramsStr = Object.keys(params)
                              .map((key) => `${key}=${params[key]}`)
                              .join('&');
                            window.open(`${cscUrl}/?${paramsStr}`, 'CscWindowShop');
                          }}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="消息中心" placement="right">
                      <Badge size="small" count={messageNum} offset={[0, 12]}>
                        <Icon
                          type="icon-xiaoxi-moren"
                          className="fle-layout-sider-action"
                          onClick={() => {
                            setMessageOpen(true);
                          }}
                        />
                      </Badge>
                    </Tooltip>
                  </div>
                </div>
                {!collapsed && (
                  <div className="fle-layout-sider-second-menu fle-scroll" ref={secondMenuRef}>
                    {activeMenuChild
                      .filter((item) => !item.hideInMenu)
                      .map((item, index) => {
                        return (
                          <div className="second-menu-group" key={item.path}>
                            {!!index && <div className="second-menu-line" />}
                            <div className="second-menu-group-title">
                              {item.icon && <Icon type={item.icon} className="second-menu-icon" />}
                              <span className="menu-group-title">{item.name}</span>
                            </div>
                            <div className="second-menu-list">
                              {item.children
                                ?.filter((route) => !route.hideInMenu)
                                .map((route) => {
                                  return (
                                    <div
                                      key={route.path}
                                      onClick={() => navigator(route.redirect || route.path)}
                                      className={classNames('second-menu-item', {
                                        active: matchMenuKeys?.includes(route.path),
                                      })}
                                    >
                                      {item.icon && (
                                        <Icon
                                          type={item.icon}
                                          className="second-menu-icon"
                                          style={{ fontSize: 16 }}
                                        />
                                      )}
                                      <span className="second-menu-name">{route.name}</span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showPageTab && <PageTabs />}

        <NoticeDrawer
          open={messageOpen}
          setMessageOpen={setMessageOpen}
          setMessageNum={setMessageNum}
        />

        <Tour
          placement="right"
          open={isTourVisible}
          steps={steps as any}
          onFinish={tourFinish}
          onClose={() => setIsTourVisible(false)}
          gap={{ offset: 5, radius: 0 }}
          indicatorsRender={() => (
            <span
              onClick={() => setIsTourVisible(false)}
              style={{ color: '#bbb', lineHeight: '22px', cursor: 'pointer' }}
            >
              跳过
            </span>
          )}
        />
      </>
    );
  },
);

SiderMenu.displayName = 'SiderMenu';

export default observer(SiderMenu);
